"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const flo_poly_1 = require("flo-poly");
const check_1 = require("./check");
const utils_1 = require("./utils");
const vec_1 = require("./vec");
/**
 * Returns an array of cubic polynomial coefficients corresponding to the
 * given cubic Bezier control-point values.
 * @private
 * @param {number} p0 First control-point value.
 * @param {number} p1 Second control-point value.
 * @param {number} p2 Third control-point value.
 * @param {number} p3 Fourth control-point value.
 * @returns {Vector4} An array containing the coefficients of t^3, t^2, t, and 1.
 */
function cubicBezierCoefficients(p0, p1, p2, p3) {
    return [
        -p0 + 3 * p1 - 3 * p2 + p3,
        3 * p0 - 6 * p1 + 3 * p2,
        -3 * p0 + 3 * p1,
        p0,
    ];
}
/**
 * Geometric functions.
 */
var geom;
(function (geom) {
    /**
     * Returns the signed area of the polygon defined by an array of points.
     * @param {Polygon2} points An array of 2D points.
     * @returns {number} The signed area of the polygon defined by the points.
     */
    function getSignedArea(points) {
        check_1.check.isArray('points', points, undefined, (name, p) => check_1.check.is2DPoint(name, p));
        // Treat fewer than three points as a special case, so the result is exactly zero.
        if (points.length < 3) {
            return 0;
        }
        const twiceArea = utils_1.utils.sum(points, (p1, i) => {
            const p2 = points[utils_1.utils.getNextIndex(i, points.length)];
            return vec_1.vec.wedge(p1, p2);
        });
        return twiceArea / 2;
    }
    geom.getSignedArea = getSignedArea;
    /**
     * Returns the orientation of a 2D polygon, given its signed area.
     * @param {number} signedArea The signed area of the polygon.
     * @returns {number} +1 for counterclockwise, -1 for clockwise, or 0 for neither.
     */
    function polygonOrientationFromSignedArea(signedArea) {
        check_1.check.isNumber('signedArea', signedArea);
        return Math.sign(signedArea);
    }
    geom.polygonOrientationFromSignedArea = polygonOrientationFromSignedArea;
    /**
     * Determines whether a point lies inside or outside an axis-aligned bounding box.
     * @param {Vector} point A point.
     * @param {Bounds} bounds An axis-aligned bounding box.
     * @returns {boolean} True if the point is inside the bounding box, otherwise false.
     */
    function isPointInsideBounds(point, bounds) {
        check_1.check.isPoint('point', point);
        check_1.check.hasProperty('bounds', bounds, 'max', (name, p) => check_1.check.isPoint(name, p));
        check_1.check.hasProperty('bounds', bounds, 'min', (name, p) => check_1.check.isPoint(name, p));
        check_1.check.isInRange('bounds.min.length', bounds.min.length, point.length, point.length);
        check_1.check.isInRange('bounds.max.length', bounds.max.length, point.length, point.length);
        return point.reduce((isInside, value, index) => isInside && value >= bounds.min[index] && value <= bounds.max[index], true);
    }
    geom.isPointInsideBounds = isPointInsideBounds;
    // TODO: there are edge cases we are not checking
    // http://alienryderflex.com/polygon/
    /**
     * Determines whether a point lies inside or outside a polygon. Points on the boundary are
     * considered outside.
     * @param {Vector2} point The 2D coordinates of a point.
     * @param {Polygon2} polygonPoints An array of 2D points defining the boundary of polygon.
     * @returns {boolean} True if the point is inside the polygon, otherwise false.
     * @see {@link http://www.eecs.umich.edu/courses/eecs380/HANDOUTS/PROJ2/InsidePoly.html}
     */
    function isPointInsidePolygon(point, polygonPoints) {
        check_1.check.is2DPoint('point', point);
        check_1.check.isArray('polygonPoints', polygonPoints, undefined, (name, p) => check_1.check.is2DPoint(name, p));
        let count = 0;
        for (let i = 0; i < polygonPoints.length; i += 1) {
            const p1 = polygonPoints[i];
            const p2 = polygonPoints[utils_1.utils.getNextIndex(i, polygonPoints.length, 1)];
            // Horizontal line segment.
            if (p1[1] === p2[1]) {
                continue;
            }
            // X coord indicates raycasting will fail.
            if (point[0] > Math.max(p1[0], p2[0])) {
                continue;
            }
            // Y coord indicates raycasting will fail.
            if (point[1] > Math.max(p1[1], p2[1]) || point[1] < Math.min(p1[1], p2[1])) {
                continue;
            }
            // Edge case when we are at same y as 1 or more endpoints of the line segment.
            if (point[1] === p1[1]) {
                continue;
            }
            if (point[1] === p2[1]) {
                // Check that the next portion of this line segment continues to opposite side of
                // point in y.
                const ySlopeThisSegment = p1[1] < p2[1] ? 1 : -1;
                // Find next segment with slope != 0.
                for (let j = 0; j < polygonPoints.length - 1; j++) {
                    const p1Next = polygonPoints[utils_1.utils.getNextIndex(i, polygonPoints.length, 1 + j)];
                    const p2Next = polygonPoints[utils_1.utils.getNextIndex(i, polygonPoints.length, 2 + j)];
                    if (p1Next[1] === p2Next[1]) {
                        continue;
                    }
                    const ySlopeNextSegment = p1Next[1] < p2Next[1] ? 1 : -1;
                    if (ySlopeThisSegment === ySlopeNextSegment) {
                        count += 1;
                    }
                    break;
                }
                continue;
            }
            const xIntersect = (((point[1] - p1[1]) * (p2[0] - p1[0])) / (p2[1] - p1[1])) + p1[0];
            if (xIntersect === point[0]) {
                return false; // on boundary
            }
            if (point[0] < xIntersect) {
                count += 1;
            }
        }
        return count % 2 === 1;
    }
    geom.isPointInsidePolygon = isPointInsidePolygon;
    /**
     * Determines whether a point lies inside or outside a region, where a region is defined as the
     * area enclosed by an outer loop of Bezier curves minus the areas (holes) enclosed by a number
     * of inner loops of Bezier curves.
     * @param {Vector2} point A 2D point.
     * @param {Bezier2[][]} region An array of closed 2D contours (the outer loop followed by
     * the inner holes), each specified by an array of Bezier curves, which in turn are defined as
     * arrays of control points. We assume (but do not check or enforce) that the outer contour
     * contains the inner contours, the inner contours are non-overlapping, and each Bezier ends at
     * the same location that the next one begins.
     * @returns {boolean} True if the point is inside the region, otherwise false.
     */
    function isPointInsideRegion(point, region) {
        check_1.check.is2DPoint('point', point);
        check_1.check.isArrayOfArrays('region', region, [1, Infinity], [1, Infinity], (name, b) => check_1.check.isArray(name, b, [2, 4], (pointName, p) => check_1.check.is2DPoint(pointName, p)));
        // Create a line starting at the given point, going in the positive X direction far enough
        // that it's guaranteed to intersect the region boundary if the point is inside.
        const flatten = (array) => array.reduce((items, item) => items.concat(item), []);
        const allPoints = flatten(region.map(flatten));
        const bounds = geom.getBounds([point, ...allPoints]);
        const size = vec_1.vec.subtract(bounds.max, bounds.min);
        const lineLength = 10 * Math.max(size[0], size[1]);
        const line = [point, [point[0] + lineLength, point[1]]];
        // Define a function that checks whether a point is inside a single closed loop.
        const isPointInsideLoop = (loop) => {
            // Count the number of intersections of the line and the Bezier curves that form the
            // loop.
            let count = 0;
            for (let i = 0; i < loop.length; i++) {
                // To avoid double-counting intersections with an endpoint shared between two
                // Beziers, we include intersections with the initial point but not the final point.
                count += geom.getIntersections(loop[i], line, 0, 0)
                    .filter(isect => isect.params[0] < 1).length;
            }
            // Use the even-odd winding rule to decide whether the point is inside the contour.
            return count % 2 === 1;
        };
        // For a point to be inside the region, it must be inside the outer loop and outside all
        // other loops.
        for (let i = 0; i < region.length; i++) {
            const isInside = isPointInsideLoop(region[i]);
            const shouldBeInside = i === 0;
            if (isInside !== shouldBeInside) {
                return false;
            }
        }
        return true;
    }
    geom.isPointInsideRegion = isPointInsideRegion;
    /**
     * Uses ray casting to determine whether a polygon lies inside or outside another polygon.
     * @param {Polygon2} innerPolygonPoints  The 2D points defining the inner polygon.
     * @param {Polygon2} outerPolygonPoints The 2D points defining the outer polygon.
     * @returns {boolean} True if the first polygon is inside the second, otherwise false.
     */
    function isPolygonInsidePolygon(innerPolygonPoints, outerPolygonPoints) {
        check_1.check.isArray('innerPolygonPoints', innerPolygonPoints, [1, Infinity], (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isArray('outerPolygonPoints', outerPolygonPoints, [1, Infinity], (name, p) => check_1.check.is2DPoint(name, p));
        // Check for an outer polygon with zero area.
        if (outerPolygonPoints.length < 3) {
            return false;
        }
        // Compute bounding boxes.
        const innerBounds = geom.getBounds(innerPolygonPoints);
        const outerBounds = geom.getBounds(outerPolygonPoints);
        const combinedMin = vec_1.vec.min(innerBounds.min, outerBounds.min);
        const combinedMax = vec_1.vec.max(innerBounds.max, outerBounds.max);
        // See if the bounding box of the inner polygon is completely contained in the bounding
        // box of the outer polygon.
        if (!vec_1.vec.equal(combinedMax, outerBounds.max)
            || !vec_1.vec.equal(combinedMin, outerBounds.min)) {
            return false;
        }
        // See if every point of the inner polygon lies within the outer polygon.
        for (let i = 0; i < innerPolygonPoints.length; i += 1) {
            const point = innerPolygonPoints[i];
            if (!geom.isPointInsidePolygon(point, outerPolygonPoints)) {
                return false;
            }
        }
        return true;
    }
    geom.isPolygonInsidePolygon = isPolygonInsidePolygon;
    /**
     * Determines whether a set of three points are collinear, within some tolerance.
     * Note that p1 and p2 should be on either side of the center point.
     * @param {Vector} center The center point.
     * @param {Vector} p1 A point on one side of the center point.
     * @param {Vector} p2 A point on the other side of the center point.
     * @param {number} collinearTolerance Amount that the dot product of two slightly non-collinear
     * unit vectors can deviate from 1 while still being considered collinear.
     * @returns {boolean} True if the three points are collinear, otherwise false.
     */
    function arePointsCollinear(center, p1, p2, collinearTolerance) {
        check_1.check.isPoint('center', center);
        check_1.check.isPoint('p1', p1);
        check_1.check.isPoint('p2', p2);
        check_1.check.isNumber('collinearTolerance', collinearTolerance);
        const vec1 = vec_1.vec.normalize(vec_1.vec.subtract(p1, center));
        const vec2 = vec_1.vec.normalize(vec_1.vec.subtract(p2, center));
        const dot = vec_1.vec.dot(vec1, vec2);
        return Math.abs(dot + 1.0) <= collinearTolerance; // dot should be near -1
    }
    geom.arePointsCollinear = arePointsCollinear;
    function getPointAverage(points) {
        check_1.check.isArray('points', points, [1, Infinity], (name, p) => check_1.check.isPoint(name, p));
        const sum = points.reduce((_sum, coord) => vec_1.vec.add(_sum, coord), vec_1.vec.fromConstant(0, points[0].length));
        return vec_1.vec.divide(sum, points.length);
    }
    geom.getPointAverage = getPointAverage;
    /**
     * Calculates the centroid (center of mass) of a simple 2D polygon.
     * @param {Polygon2} points An array of 2D points defining a polygon.
     * @returns {Vector2} The centroid (center of mass) of the polygon.
     * @see {@link https://en.wikipedia.org/wiki/Centroid#Of_a_polygon}
     */
    function getPolygonCentroid(points) {
        check_1.check.isArray('points', points, [1, Infinity], (name, p) => check_1.check.isPoint(name, p));
        const n = points.length;
        if (n < 3) {
            return geom.getPointAverage(points);
        }
        let twiceArea = 0;
        let weightedPoint = [0, 0];
        points.forEach((p1, i) => {
            const p2 = points[utils_1.utils.getNextIndex(i, n)];
            const f = p1[0] * p2[1] - p2[0] * p1[1];
            twiceArea += f;
            weightedPoint = vec_1.vec.add(weightedPoint, vec_1.vec.multiply(f, vec_1.vec.add(p1, p2)));
        });
        return vec_1.vec.divide(weightedPoint, 3 * twiceArea);
    }
    geom.getPolygonCentroid = getPolygonCentroid;
    function getBounds(points) {
        check_1.check.isArray('points', points, [1, Infinity], (name, p) => check_1.check.isPoint(name, p));
        let min = vec_1.vec.fromConstant(Infinity, points[0].length);
        let max = vec_1.vec.fromConstant(-Infinity, points[0].length);
        points.forEach((point) => {
            min = vec_1.vec.min(min, point);
            max = vec_1.vec.max(max, point);
        });
        return { min, max };
    }
    geom.getBounds = getBounds;
    /**
     * Returns the four corner points of an axis-aligned bounding box.
     * @param {Bounds2} bounds An axis-aligned 2D bounding box.
     * @returns {Quad2} The four corner points of the axis-aligned bounding box.
     */
    function getPolygonFromBounds(bounds) {
        check_1.check.hasProperty('bounds', bounds, 'max', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.hasProperty('bounds', bounds, 'min', (name, p) => check_1.check.is2DPoint(name, p));
        return [
            bounds.min,
            [bounds.max[0], bounds.min[1]],
            bounds.max,
            [bounds.min[0], bounds.max[1]],
        ];
    }
    geom.getPolygonFromBounds = getPolygonFromBounds;
    /**
     * Indicates whether two 2D axis-aligned bounding boxes intersect.
     * @param {Bounds2} bounds1 The first bounds.
     * @param {Bounds2} bounds2 The second bounds.
     * @param {number} tolerance The tolerance to use in intersection testing (can be negative).
     * @returns {boolean} True if the bounds intersect, false otherwise.
     */
    function doBoundsIntersect(bounds1, bounds2, tolerance) {
        check_1.check.hasProperty('bounds1', bounds1, 'max', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.hasProperty('bounds1', bounds1, 'min', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.hasProperty('bounds2', bounds2, 'min', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.hasProperty('bounds2', bounds2, 'max', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isNumber('tolerance', tolerance);
        if (bounds1.max[0] + tolerance < bounds2.min[0]
            || bounds1.max[1] + tolerance < bounds2.min[1]
            || bounds2.max[0] + tolerance < bounds1.min[0]
            || bounds2.max[1] + tolerance < bounds1.min[1]) {
            return false;
        }
        return true;
    }
    geom.doBoundsIntersect = doBoundsIntersect;
    /**
     * Indicates whether an axis-aligned bounding box intersects a Bezier curve.
     * @param {Bounds2} bounds An axis-aligned bounding box.
     * @param {Bezier2} bezier The control points of the Bezier curve.
     * @returns {boolean} True if the bounds intersect the Bezier, false otherwise.
     */
    function doBoundsIntersectBezier(bounds, bezier) {
        check_1.check.hasProperty('bounds', bounds, 'max', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.hasProperty('bounds', bounds, 'min', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isArray('bezier', bezier, [2, Infinity], (name, p) => check_1.check.is2DPoint(name, p));
        // If either of the Bezier endpoints is inside the bounds, there's an intersection.
        for (let i = 0; i < bezier.length; i += bezier.length - 1) {
            if (geom.isPointInsideBounds(bezier[i], bounds)) {
                return true;
            }
        }
        // If the Bezier intersects any of the bounding box edges, there's an intersection.
        const boundsPolygon = geom.getPolygonFromBounds(bounds);
        const boundsEdges = boundsPolygon.map((p, i) => [p, boundsPolygon[(i + 1) % boundsPolygon.length]]);
        for (let i = 0; i < boundsEdges.length; i++) {
            const intersections = geom.getIntersections(bezier, boundsEdges[i], 0, 0);
            if (intersections.length) {
                return true;
            }
        }
        // Otherwise, there's no intersection.
        return false;
    }
    geom.doBoundsIntersectBezier = doBoundsIntersectBezier;
    /**
     * Indicates whether an axis-aligned bounding box intersects a polygon.
     * @param {Bounds2} bounds An axis-aligned bounding box.
     * @param {Polygon2} polygon An array of points defining a polygon.
     * @returns {boolean} True if the bounds intersect the polygon, false otherwise.
     */
    function doBoundsIntersectPolygon(bounds, polygon) {
        check_1.check.hasProperty('bounds', bounds, 'max', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.hasProperty('bounds', bounds, 'min', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isArray('polygon', polygon, [1, Infinity], (name, p) => check_1.check.is2DPoint(name, p));
        // If any of the polygon points is inside the bounding box, there's an intersection.
        for (let i = 0; i < polygon.length; i++) {
            if (geom.isPointInsideBounds(polygon[i], bounds)) {
                return true;
            }
        }
        // If any corner of the bounding box is inside the polygon, there's an intersection.
        const boundsPolygon = geom.getPolygonFromBounds(bounds);
        for (let i = 0; i < boundsPolygon.length; i++) {
            if (geom.isPointInsidePolygon(boundsPolygon[i], polygon)) {
                return true;
            }
        }
        // If any of the polygon edges intersects any of the bounding box edges, there's an
        // intersection.
        const boundsEdges = boundsPolygon.map((p, i) => [p, boundsPolygon[(i + 1) % boundsPolygon.length]]);
        for (let i = 0; i < polygon.length; i++) {
            const line = [polygon[i], polygon[(i + 1) % polygon.length]];
            for (let j = 0; j < boundsEdges.length; j++) {
                const intersections = geom.lineLineIntersection(line, boundsEdges[j], 0, 0);
                if (intersections.length) {
                    return true;
                }
            }
        }
        // Otherwise, there's no intersection.
        return false;
    }
    geom.doBoundsIntersectPolygon = doBoundsIntersectPolygon;
    /**
     * Indicates whether an axis-aligned bounding box intersects a region, where a region is
     * defined as the area enclosed by an outer loop of Bezier curves minus the areas (holes)
     * enclosed by a number of inner loops of Bezier curves.
     * @param {Bounds2} bounds An axis-aligned bounding box.
     * @param {Bezier2[][]} region An array of closed 2D contours (the outer loop followed by
     * the inner holes), each specified by an array of Bezier curves, which in turn are defined as
     * arrays of control points. We assume (but do not check or enforce) that the outer contour
     * contains the inner contours, the inner contours are non-overlapping, and each Bezier ends at
     * the same location that the next one begins.
     * @returns {boolean} True if the bounds intersect the region, false otherwise.
     */
    function doBoundsIntersectRegion(bounds, region) {
        check_1.check.hasProperty('bounds', bounds, 'max', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.hasProperty('bounds', bounds, 'min', (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isArrayOfArrays('region', region, [1, Infinity], [1, Infinity], (name, b) => check_1.check.isArray(name, b, [2, 4], (pointName, p) => check_1.check.is2DPoint(pointName, p)));
        // If any of the Bezier curves intersects the area enclosed by the bounding box, there's
        // an intersection.
        for (let i = 0; i < region.length; i++) {
            const loop = region[i];
            for (let j = 0; j < loop.length; j++) {
                if (geom.doBoundsIntersectBezier(bounds, loop[j])) {
                    return true;
                }
            }
        }
        // If any corner of the bounding box is inside the region, there's an intersection.
        const boundsPolygon = geom.getPolygonFromBounds(bounds);
        for (let i = 0; i < boundsPolygon.length; i++) {
            if (geom.isPointInsideRegion(boundsPolygon[i], region)) {
                return true;
            }
        }
        // Otherwise, there's no intersection.
        return false;
    }
    geom.doBoundsIntersectRegion = doBoundsIntersectRegion;
    /**
     * Returns an array of intersection objects.
     * @param {Bezier2} bezier1 Array of two, three, or four 2D Bezier control points.
     * @param {Bezier2} bezier2 Array of two, three, or four 2D Bezier control points.
     * @param {number} distanceTolerance The maximum distance between two curves or lines that are
     * still considered intersecting.
     * @param {number} angleTolerance The maximum angle (in radians) between two lines that are
     * still considered parallel.
     * @returns {Intersection[]} An array of intersection objects, each containing two parameter
     * values and two positions.
     */
    function getIntersections(bezier1, bezier2, distanceTolerance, angleTolerance) {
        check_1.check.isArray('bezier1', bezier1, [2, 4], (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isArray('bezier2', bezier2, [2, 4], (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isInRange('distanceTolerance', distanceTolerance, 0, Infinity);
        check_1.check.isInRange('angleTolerance', angleTolerance, 0, Math.PI / 2);
        // First see if the bounds intersect.
        const bounds1 = geom.getBounds(bezier1);
        const bounds2 = geom.getBounds(bezier2);
        if (!geom.doBoundsIntersect(bounds1, bounds2, distanceTolerance)) {
            return [];
        }
        // Check for special cases.
        // TODO: Quadratic Bezier cases.
        if (bezier1.length === 2 && bezier2.length === 2) {
            // Line-line intersection.
            return geom.lineLineIntersection(bezier1, bezier2, distanceTolerance, angleTolerance);
        }
        if (bezier1.length === 2 && bezier2.length === 4) {
            // Line-cubic intersection.
            return geom.lineCubicIntersection(bezier1, bezier2, distanceTolerance);
        }
        if (bezier1.length === 4 && bezier2.length === 2) {
            // Cubic-line intersection.
            const result = geom.lineCubicIntersection(bezier2, bezier1, distanceTolerance);
            result.forEach((intersection) => {
                intersection.positions.reverse();
                intersection.params.reverse();
            });
            return result;
        }
        if (bezier1.length === 4 && bezier2.length === 4) {
            // Cubic-cubic intersection.
            return geom.cubicCubicIntersection(bezier1, bezier2, distanceTolerance);
        }
        throw new Error(`Intersection between Beziers of degree ${bezier1.length - 1} and ${bezier2.length - 1} is not implemented.`);
    }
    geom.getIntersections = getIntersections;
    /**
     * Finds intersections between two 2D line segments.
     * @param {Line2} line1 The two endpoints of the first line segment.
     * @param {Line2} line2 The two endpoints of the second line segment.
     * @param {number} distanceTolerance The maximum distance between two curves or lines that are
     * still considered intersecting.
     * @param {number} angleTolerance The maximum angle (in radians) between two lines that are
     * still considered parallel.
     * @returns {Intersection[]} An array of intersection objects, each containing two parameter
     * values and two positions.
     * @see {@link http://paulbourke.net/geometry/pointlineplane/}
     */
    function lineLineIntersection(line1, line2, distanceTolerance, angleTolerance) {
        check_1.check.isArray('line1', line1, 2, (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isArray('line2', line2, 2, (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isInRange('distanceTolerance', distanceTolerance, 0, Infinity);
        check_1.check.isInRange('angleTolerance', angleTolerance, 0, Math.PI / 2);
        const p1a = line1[0];
        const p1b = line1[1];
        const p2a = line2[0];
        const p2b = line2[1];
        const v1 = vec_1.vec.subtract(p1b, p1a);
        const v2 = vec_1.vec.subtract(p2b, p2a);
        const x1 = p1a[0];
        const y1 = p1a[1];
        const x2 = p1b[0];
        const y2 = p1b[1];
        const x3 = p2a[0];
        const y3 = p2a[1];
        const x4 = p2b[0];
        const y4 = p2b[1];
        // Check line lengths.
        const squaredLength1 = vec_1.vec.lengthSquared(v1);
        const squaredLength2 = vec_1.vec.lengthSquared(v2);
        const distTolSq = distanceTolerance * distanceTolerance;
        if (squaredLength1 <= distTolSq || squaredLength2 <= distTolSq) {
            return [];
        }
        const derivs1 = geom.getBezierEndpointDerivatives(line1);
        const derivs2 = geom.getBezierEndpointDerivatives(line2);
        // Check for parallel lines.
        const denom = ((y4 - y3) * (x2 - x1)) - ((x4 - x3) * (y2 - y1));
        const dot = denom / (Math.sqrt(squaredLength1) * Math.sqrt(squaredLength2));
        if (Math.abs(dot) <= Math.sin(angleTolerance)) {
            // The lines are parallel. Check to see if they overlap.
            const n1 = vec_1.vec.normalize([v1[1], -v1[0]]);
            const d2a = vec_1.vec.dot(vec_1.vec.subtract(p2a, p1a), n1);
            const d2b = vec_1.vec.dot(vec_1.vec.subtract(p2b, p1a), n1);
            if (Math.abs(d2a) > distanceTolerance || Math.abs(d2b) > distanceTolerance) {
                return [];
            }
            // Compute parameters of p2a and p2b on line1, and of p1a and p1b on line2.
            const t1b = vec_1.vec.dot(vec_1.vec.subtract(p2b, p1a), v1) / squaredLength1;
            const t1a = vec_1.vec.dot(vec_1.vec.subtract(p2a, p1a), v1) / squaredLength1;
            const t2a = vec_1.vec.dot(vec_1.vec.subtract(p1a, p2a), v2) / squaredLength2;
            const t2b = vec_1.vec.dot(vec_1.vec.subtract(p1b, p2a), v2) / squaredLength2;
            // Check to see if parameters are outside the valid range or on endpoints.
            const t1aSnapped = geom.snapIntersectionParameterToEndpoint(t1a, derivs1, distanceTolerance);
            const t1bSnapped = geom.snapIntersectionParameterToEndpoint(t1b, derivs1, distanceTolerance);
            const t2aSnapped = geom.snapIntersectionParameterToEndpoint(t2a, derivs2, distanceTolerance);
            const t2bSnapped = geom.snapIntersectionParameterToEndpoint(t2b, derivs2, distanceTolerance);
            // Record an intersection for each endpoint that's within the other edge.
            const intersections = [];
            if (t1aSnapped !== null) {
                const q1a = geom.evaluateBezier(line1, t1aSnapped);
                intersections.push({ params: [t1aSnapped, 0], positions: [q1a, p2a] });
            }
            if (t1bSnapped !== null) {
                const q1b = geom.evaluateBezier(line1, t1bSnapped);
                intersections.push({ params: [t1bSnapped, 1], positions: [q1b, p2b] });
            }
            if (t2aSnapped !== null && t2aSnapped !== 0 && t2aSnapped !== 1) {
                const q2a = geom.evaluateBezier(line2, t2aSnapped);
                intersections.push({ params: [0, t2aSnapped], positions: [p1a, q2a] });
            }
            if (t2bSnapped !== null && t2bSnapped !== 0 && t2bSnapped !== 1) {
                const q2b = geom.evaluateBezier(line2, t2bSnapped);
                intersections.push({ params: [1, t2bSnapped], positions: [p1b, q2b] });
            }
            return intersections;
        }
        // Compute the parametric coordinates of the intersection on each of the lines.
        const t1 = (((x4 - x3) * (y1 - y3)) - ((y4 - y3) * (x1 - x3))) / denom;
        const t2 = (((x2 - x1) * (y1 - y3)) - ((y2 - y1) * (x1 - x3))) / denom;
        // Check to see if parameters are outside the valid range or on endpoints.
        const t1Snapped = geom.snapIntersectionParameterToEndpoint(t1, derivs1, distanceTolerance);
        const t2Snapped = geom.snapIntersectionParameterToEndpoint(t2, derivs2, distanceTolerance);
        // If both parameters are valid, return the intersection.
        if (t1Snapped !== null && t2Snapped !== null) {
            const q1 = geom.evaluateBezier(line1, t1Snapped);
            const q2 = geom.evaluateBezier(line2, t2Snapped);
            return [{ params: [t1Snapped, t2Snapped], positions: [q1, q2] }];
        }
        return [];
    }
    geom.lineLineIntersection = lineLineIntersection;
    /**
     * Finds intersections between a line segment and a cubic Bezier curve.
     * @param {Line2} line An array of two line segment endpoints.
     * @param {CubicBezier2} cubicBezier An array of four cubic Bezier control points.
     * @param {number} distanceTolerance The maximum distance between two curves or lines that are
     * still considered intersecting.
     * @returns {Intersection[]} An array of intersection objects, each containing two parameter
     * values and two positions.
     */
    function lineCubicIntersection(line, cubicBezier, distanceTolerance) {
        check_1.check.isArray('line', line, 2, (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isArray('cubicBezier', cubicBezier, 4, (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isInRange('distanceTolerance', distanceTolerance, 0, Infinity);
        // First check for a zero-length line.
        const p1a = line[0];
        const p1b = line[1];
        const v1 = vec_1.vec.subtract(p1b, p1a);
        const squaredLength1 = vec_1.vec.lengthSquared(v1);
        if (squaredLength1 < distanceTolerance * distanceTolerance) {
            return [];
        }
        // Calculate the coefficients of a cubic polynomial.
        const a = v1[1]; // a = y2 - y1
        const b = -v1[0]; // b = x1 - x2
        const c = -a * p1a[0] - b * p1a[1]; // c = x1 * (y1 - y2) + y1 * (x2 - x1)
        const bx = cubicBezierCoefficients(cubicBezier[0][0], cubicBezier[1][0], cubicBezier[2][0], cubicBezier[3][0]);
        const by = cubicBezierCoefficients(cubicBezier[0][1], cubicBezier[1][1], cubicBezier[2][1], cubicBezier[3][1]);
        const poly = [
            a * bx[0] + b * by[0],
            a * bx[1] + b * by[1],
            a * bx[2] + b * by[2],
            a * bx[3] + b * by[3] + c,
        ];
        // Find all real roots of the cubic polynomial.
        const roots = flo_poly_1.allRoots(poly);
        // Iterate over these roots to come up with intersection points.
        const intersections = [];
        roots.forEach((t2) => {
            // Exclude roots that are outside the unit interval.
            const derivs2 = geom.getBezierEndpointDerivatives(cubicBezier);
            const t2Snapped = geom.snapIntersectionParameterToEndpoint(t2, derivs2, distanceTolerance);
            if (t2Snapped !== null) {
                // Evaluate the cubic Bezier to get the intersection point.
                const q2 = geom.evaluateBezier(cubicBezier, t2Snapped);
                // Get the parameter along the line.
                const t1 = vec_1.vec.dot(vec_1.vec.subtract(q2, p1a), v1) / squaredLength1;
                // Check to see if t1 is outside the valid range or on an endpoint.
                const derivs1 = geom.getBezierEndpointDerivatives(line);
                const t1Snapped = geom.snapIntersectionParameterToEndpoint(t1, derivs1, distanceTolerance);
                // Exclude points that are beyond the line segment endpoints.
                if (t1Snapped !== null) {
                    // Add the intersection to the list of results.
                    const q1 = geom.evaluateBezier(line, t1Snapped);
                    intersections.push({ params: [t1Snapped, t2Snapped], positions: [q1, q2] });
                }
            }
        });
        // Special case: if no intersections were found, see if either cubic endpoint is within
        // distance tolerance of line. (Fixes SVGToCreaseSpec test for "bad-parse.svg".)
        if (intersections.length === 0) {
            const endpoints = [
                { t2: 0, q2: cubicBezier[0] },
                { t2: 1, q2: cubicBezier[3] },
            ];
            endpoints.forEach((endpoint) => {
                const { t2, q2 } = endpoint;
                const t1 = vec_1.vec.dot(vec_1.vec.subtract(q2, p1a), v1) / squaredLength1;
                // Check to see if t1 is outside the valid range or on an endpoint.
                const derivs1 = geom.getBezierEndpointDerivatives(line);
                const t1Snapped = geom.snapIntersectionParameterToEndpoint(t1, derivs1, distanceTolerance);
                // Exclude points that are beyond the line segment endpoints.
                if (t1Snapped !== null) {
                    // See whether the closest point on the line is within the distance tolerance
                    // of the cubic endpoint.
                    const q1 = geom.evaluateBezier(line, t1Snapped);
                    if (vec_1.vec.distance(q1, q2) < distanceTolerance) {
                        // Add the intersection to the list of results.
                        intersections.push({ params: [t1Snapped, t2], positions: [q1, q2] });
                    }
                }
            });
        }
        // Return all intersections.
        return intersections;
    }
    geom.lineCubicIntersection = lineCubicIntersection;
    /**
     * Finds intersections between two 2D cubic Bezier segments.
     * @param {CubicBezier2} cubic1 The control points of the first cubic Bezier.
     * @param {CubicBezier2} cubic2 The control points of the second cubic Bezier.
     * @param {number} distanceTolerance The maximum distance between two curves or lines that are
     * still considered intersecting.
     * @returns {Intersection[]} An array of intersection objects, each containing two parameter
     * values and two positions.
     */
    function cubicCubicIntersection(cubic1, cubic2, distanceTolerance) {
        check_1.check.isArray('cubic1', cubic1, 4, (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isArray('cubic2', cubic2, 4, (name, p) => check_1.check.is2DPoint(name, p));
        check_1.check.isInRange('distanceTolerance', distanceTolerance, 0, Infinity);
        // TODO: Not implemented yet.
        return [];
    }
    geom.cubicCubicIntersection = cubicCubicIntersection;
    /**
     * Evaluates the first and second derivative at the start and end of a Bezier curve of any
     * degree. Note that for a line segment (a Bezier with two control points), both first
     * derivatives are simply the length of the segment, and both second derivatives are zero.
     * @param {Bezier2} bezier An array of Bezier control points.
     * @returns {BezierEndpointDerivatives} An object containing the first and second derivatives
     * at the start and end of the Bezier.
     * @see {@link https://stackoverflow.com/questions/16140281/how-to-determine-curvature-of-a-cubic-bezier-path-at-an-end-point}
     */
    function getBezierEndpointDerivatives(bezier) {
        check_1.check.isArray('bezier', bezier, [2, Infinity], (name, p) => check_1.check.isPoint(name, p));
        const n = bezier.length - 1;
        const startTangent = vec_1.vec.subtract(bezier[1], bezier[0]);
        const endTangent = vec_1.vec.subtract(bezier[n - 1], bezier[n]);
        const derivatives = {
            dtStart: vec_1.vec.multiply(startTangent, n),
            dtEnd: vec_1.vec.multiply(endTangent, n),
            ddtStart: [0, 0],
            ddtEnd: [0, 0],
        };
        if (n > 1) {
            const quadraticBezier = bezier; // keep TypeScript happy
            const bStart = vec_1.vec.subtract(vec_1.vec.subtract(quadraticBezier[2], bezier[1]), startTangent);
            const bEnd = vec_1.vec.subtract(vec_1.vec.subtract(bezier[n - 2], bezier[n - 1]), endTangent);
            derivatives.ddtStart = vec_1.vec.multiply(bStart, n * (n - 1));
            derivatives.ddtEnd = vec_1.vec.multiply(bEnd, n * (n - 1));
        }
        return derivatives;
    }
    geom.getBezierEndpointDerivatives = getBezierEndpointDerivatives;
    /**
     * Modifies the given Bezier curve parameter based on how close it is to the ends of the unit
     * interval, relative to the speeds (tangent magnitudes) at the curve endpoints.
     * @param {number} t Parameter value of the intersection point.
     * @param {BezierEndpointDerivatives} derivatives Derivatives wrt t
     * at the curve endpoints.
     * @param {number} distanceTolerance The maximum distance between two curves or lines that are
     * still considered intersecting.
     * @returns {number | null} The modified parameter value, or null to indicate no valid
     * intersection.
     */
    function snapIntersectionParameterToEndpoint(t, derivatives, distanceTolerance) {
        check_1.check.isNumber('t', t);
        check_1.check.hasProperty('derivatives', derivatives, 'dtStart', (name, deriv) => check_1.check.isArray(name, deriv, 2, (elName, el) => check_1.check.isNumber(elName, el)));
        check_1.check.hasProperty('derivatives', derivatives, 'dtEnd', (name, deriv) => check_1.check.isArray(name, deriv, 2, (elName, el) => check_1.check.isNumber(elName, el)));
        check_1.check.hasProperty('derivatives', derivatives, 'ddtStart', (name, deriv) => check_1.check.isArray(name, deriv, 2, (elName, el) => check_1.check.isNumber(elName, el)));
        check_1.check.hasProperty('derivatives', derivatives, 'ddtEnd', (name, deriv) => check_1.check.isArray(name, deriv, 2, (elName, el) => check_1.check.isNumber(elName, el)));
        check_1.check.isInRange('distanceTolerance', distanceTolerance, 0, Infinity);
        const u = 1 - t;
        // Taylor series approximation using first derivative.
        let startDistance = vec_1.vec.length(vec_1.vec.multiply(t, derivatives.dtStart));
        let endDistance = vec_1.vec.length(vec_1.vec.multiply(u, derivatives.dtEnd));
        // Out of bounds.
        if ((startDistance > distanceTolerance && t < 0)
            || (endDistance > distanceTolerance && u < 0)) {
            return null;
        }
        if (startDistance <= distanceTolerance) {
            // Taylor series approximation using first and second derivatives.
            startDistance = vec_1.vec.length(vec_1.vec.add(vec_1.vec.multiply(t, derivatives.dtStart), vec_1.vec.multiply(t * t / 2, derivatives.ddtStart)));
            if (startDistance <= distanceTolerance) {
                return 0;
            }
            if (t < 0) {
                return null;
            }
        }
        if (endDistance <= distanceTolerance) {
            // Taylor series approximation using first and second derivatives.
            endDistance = vec_1.vec.length(vec_1.vec.add(vec_1.vec.multiply(u, derivatives.dtEnd), vec_1.vec.multiply(u * u / 2, derivatives.ddtEnd)));
            if (endDistance <= distanceTolerance) {
                return 1;
            }
            if (u < 0) {
                return null;
            }
        }
        return t;
    }
    geom.snapIntersectionParameterToEndpoint = snapIntersectionParameterToEndpoint;
    function evaluateBezier(bezier, t) {
        check_1.check.isArray('bezier', bezier, undefined, (name, p) => check_1.check.isPoint(name, p));
        check_1.check.isNumber('t', t);
        const n = bezier.length;
        const p = bezier.slice();
        const s = 1 - t;
        for (let i = n - 1; i > 0; i--) {
            for (let j = 0; j < i; j++) {
                p[j] = vec_1.vec.add(vec_1.vec.multiply(s, p[j]), vec_1.vec.multiply(t, p[j + 1]));
            }
        }
        return p[0];
    }
    geom.evaluateBezier = evaluateBezier;
    /**
     * Evaluates the starting tangent of a 2D Bezier.
     * @param {Bezier2} bezier An array of 2D Bezier control points.
     * @returns {Vector2} The tangent vector at the start of the Bezier.
     */
    function evaluateBezierStartTangent(bezier) {
        check_1.check.isArray('bezier', bezier, undefined, (name, p) => check_1.check.is2DPoint(name, p));
        let tangent = [0, 0];
        for (let i = 1; i < bezier.length; i++) {
            tangent = vec_1.vec.subtract(bezier[i], bezier[0]);
            if (tangent[0] !== 0 || tangent[1] !== 0) {
                break;
            }
        }
        return tangent;
    }
    geom.evaluateBezierStartTangent = evaluateBezierStartTangent;
    /**
     * Splits a Bezier curve of any degree into two curves at the specified parameter value.
     * @param {Bezier} bezier An array of Bezier control points.
     * @param {number} t Parameter value along the Bezier curve.
     * @returns {{ left: Bezier, right: Bezier }} The control points
     * of the left and right Bezier curves.
     * @see {@link https://en.wikipedia.org/wiki/De_Casteljau%27s_algorithm}
     */
    function splitBezier(bezier, t) {
        check_1.check.isArray('bezier', bezier, undefined, (name, p) => check_1.check.isPoint(name, p));
        check_1.check.isInRange('t', t, 0, 1);
        const n = bezier.length;
        const left = bezier.slice();
        const right = bezier.slice();
        for (let i = 1; i < n; i++) {
            let previous = left[i - 1];
            for (let j = i; j < n; j++) {
                const current = left[j];
                left[j] = geom.evaluateBezier([previous, current], t);
                right[j - i] = left[j];
                previous = current;
            }
        }
        return { left, right };
    }
    geom.splitBezier = splitBezier;
    // TODO: write tests
    /**
     * Returns a point that lies inside the given 2D polygon.
     * @param {Polygon2} polygonPoints An array of 2D points defining a polygon.
     * @returns {Vector2} A point within the polygon.
     * @see {@link https://mathoverflow.net/questions/56655/get-a-point-inside-a-polygon}
     */
    function createPointInsidePolygon(polygonPoints) {
        if (polygonPoints.length < 3) {
            throw new Error('Polygon must contain at least three points.');
        }
        if (geom.getSignedArea(polygonPoints) === 0) {
            throw new Error('Polygon must have nonzero area.');
        }
        // find an xCoord that is far away from existing xCoords
        const xCoords = polygonPoints.map(coord => coord[0]);
        xCoords.sort(utils_1.utils.sortNumberCompare);
        // find max spacing between two adjacent x coords
        let maxSpacing = 0;
        let index = 0;
        xCoords.forEach((coord, i) => {
            if (i === xCoords.length - 1) {
                return;
            }
            const spacing = xCoords[i + 1] - coord;
            if (spacing > maxSpacing) {
                maxSpacing = spacing;
                index = i;
            }
        });
        if (maxSpacing === 0) {
            throw new Error('Polygon must have nonzero area.');
        }
        const x = xCoords[index] + maxSpacing / 2;
        // use a vertical line at x to find intersection with polygon
        const intersections = [];
        polygonPoints.forEach((p1, i) => {
            const p2 = polygonPoints[utils_1.utils.getNextIndex(i, polygonPoints.length)];
            // check bounds
            if (x > Math.max(p1[0], p2[0])) {
                return;
            }
            if (x < Math.min(p1[0], p2[0])) {
                return;
            }
            // check for vertical line
            if (p1[0] === p2[0]) {
                return;
            }
            const yIntercept = (x - p1[0]) * (p2[1] - p1[1]) / (p2[0] - p1[0]) + p1[1];
            intersections.push(yIntercept);
        });
        if (intersections.length === 0) {
            throw new Error(`No valid intersections found with vertical line at ${x}.`);
        }
        if (intersections.length % 2 !== 0) {
            throw new Error('Invalid polygon intersections.');
        }
        // order intersections by y
        intersections.sort(utils_1.utils.sortNumberCompare);
        if (intersections[1] - intersections[0] < Number.MIN_VALUE * 3) {
            throw new Error('Distance between first two intersection points is too small.');
        }
        // pt in polygon is midpoint of first two intersection pts
        return [x, (intersections[1] + intersections[0]) / 2];
    }
    geom.createPointInsidePolygon = createPointInsidePolygon;
})(geom = exports.geom || (exports.geom = {}));
//# sourceMappingURL=geom.js.map